import React, { useCallback, useEffect, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import styles from './UIGameAchievement.module.scss';
import { getAchievementData } from './type.ts';
import { AudioManager } from '@colonist/audio';
import { Avatar } from '../../common/Avatar/index.ts';
import { T } from '@colonist/ui';
import clsx from 'clsx';
import { sleep } from '../../utils/helpers.ts';
import { getNestedTranslation, passThroughTranslation } from '@colonist/i18n';
import IconTradeArrowGreen from '@static/images/icon_trade_arrow_green.svg';
const getVPChangeText = (increase = true, victoryPointChange)=>{
    if (victoryPointChange == null || victoryPointChange === 0) {
        return null;
    }
    const operationSign = increase ? '+' : '-';
    return getNestedTranslation({
        key: 'strings:colonistCommon.utils.#append'
    }, {
        value1: passThroughTranslation(operationSign),
        value2: {
            key: 'strings:game.playerInformation.victoryPoint.countAbbreviated',
            options: {
                count: victoryPointChange
            }
        }
    });
};
const DISAPPEAR_ANIMATION_DURATION = 1000;
export const UIGameAchievement = ({ durationMs = 5000, onDisappear, isConfettiEnabled, achievementOwnerNew, achievementOwnerPrevious, achievementType, achievementScore, translationContext, customAudio })=>{
    const achievementData = getAchievementData(achievementType, achievementOwnerNew.selectedColor, translationContext);
    const [isHidden, setIsHidden] = useState(false);
    const isDisappearingRef = useRef(false);
    const triggerDisappear = useCallback(async ()=>{
        if (isDisappearingRef.current) return;
        isDisappearingRef.current = true;
        setIsHidden(true);
        await sleep(DISAPPEAR_ANIMATION_DURATION);
        onDisappear();
    }, [
        onDisappear
    ]);
    const runAnimation = async ()=>{
        if (customAudio != null) {
            AudioManager.play(customAudio);
        } else if (achievementData.soundType != null) {
            AudioManager.play(achievementData.soundType);
        }
        await sleep(durationMs);
        void triggerDisappear();
    };
    useEffect(()=>{
        void runAnimation();
    }, []);
    const handleClick = useCallback(()=>{
        void triggerDisappear();
    }, [
        triggerDisappear
    ]);
    const renderAchievementSubtitle = ()=>{
        if (achievementData.subTitle) {
            return /*#__PURE__*/ React.createElement("div", {
                className: styles.subTitle
            }, /*#__PURE__*/ React.createElement(T, {
                id: achievementData.subTitle
            }));
        }
        const vpChangeText = getVPChangeText(true, achievementData.victoryPointChange);
        const hasVPChange = vpChangeText != null;
        if (achievementOwnerPrevious != null) {
            const vpChangeTextPrevious = getVPChangeText(false, achievementData.victoryPointChange);
            const hasVPChangePrevious = vpChangeTextPrevious != null;
            return /*#__PURE__*/ React.createElement("div", {
                className: clsx(styles.pointChanges, styles.withPreviousOwner)
            }, /*#__PURE__*/ React.createElement(Avatar, {
                className: styles.avatar,
                color: achievementOwnerPrevious.selectedColor,
                image: achievementOwnerPrevious.databaseIcon
            }), /*#__PURE__*/ React.createElement("div", {
                className: styles.playerPointChange
            }, /*#__PURE__*/ React.createElement("span", {
                className: styles.playerName
            }, achievementOwnerPrevious.username), hasVPChangePrevious && /*#__PURE__*/ React.createElement("span", {
                className: clsx(styles.points, styles.negative)
            }, /*#__PURE__*/ React.createElement(T, {
                id: vpChangeTextPrevious
            }))), /*#__PURE__*/ React.createElement("img", {
                className: styles.transferIcon,
                src: IconTradeArrowGreen
            }), /*#__PURE__*/ React.createElement(Avatar, {
                className: styles.avatar,
                color: achievementOwnerNew.selectedColor,
                image: achievementOwnerNew.databaseIcon
            }), /*#__PURE__*/ React.createElement("div", {
                className: styles.playerPointChange
            }, /*#__PURE__*/ React.createElement("span", {
                className: styles.playerName
            }, achievementOwnerNew.username), hasVPChange && /*#__PURE__*/ React.createElement("span", {
                className: styles.points
            }, /*#__PURE__*/ React.createElement(T, {
                id: vpChangeText
            }))));
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: styles.pointChanges
        }, /*#__PURE__*/ React.createElement(Avatar, {
            className: styles.avatar,
            color: achievementOwnerNew.selectedColor,
            image: achievementOwnerNew.databaseIcon
        }), /*#__PURE__*/ React.createElement("span", {
            className: styles.playerName
        }, achievementOwnerNew.username), hasVPChange && /*#__PURE__*/ React.createElement("span", {
            className: styles.points
        }, /*#__PURE__*/ React.createElement(T, {
            id: vpChangeText
        })));
    };
    const showAchievementScore = achievementScore != null;
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.container, {
            [styles.hidden]: isHidden
        }),
        style: {
            transitionDuration: `${DISAPPEAR_ANIMATION_DURATION}ms`
        },
        onClick: handleClick
    }, isConfettiEnabled && /*#__PURE__*/ React.createElement(Confetti, {
        className: styles.confetti,
        initialVelocityX: 10,
        initialVelocityY: -10,
        numberOfPieces: 175,
        recycle: false
    }), /*#__PURE__*/ React.createElement("div", {
        className: styles.header
    }, /*#__PURE__*/ React.createElement(T, {
        id: achievementData.title
    }), /*#__PURE__*/ React.createElement("div", {
        className: styles.iconWrapper
    }, /*#__PURE__*/ React.createElement("img", {
        src: achievementData.icon
    }), achievementData.subIcon != null && /*#__PURE__*/ React.createElement("img", {
        className: styles.subIcon,
        src: achievementData.subIcon
    }), showAchievementScore && /*#__PURE__*/ React.createElement("span", {
        className: styles.point
    }, achievementScore))), renderAchievementSubtitle());
};
export default UIGameAchievement;
